import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Invoice, Project } from '../types';

const statusLabels: Record<string, string> = {
  DRAFT: 'Entwurf', SENT: 'Gesendet', ACCEPTED: 'Angenommen', REJECTED: 'Abgelehnt', CANCELLED: 'Storniert',
};
const typeLabels: Record<string, string> = {
  NORMAL: 'Standard', PARTIAL: 'Abschlag', FINAL: 'Schluss',
};
const typeColors: Record<string, string> = {
  NORMAL: 'bg-slate-50 text-slate-600 border-slate-200',
  PARTIAL: 'bg-blue-50 text-blue-700 border-blue-200',
  FINAL: 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function InvoicesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Invoice> | null>(null);

  const { data: invoices = [], isLoading } = useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => api.get('/invoices').then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (inv: Partial<Invoice>) =>
      inv.id ? api.put(`/invoices/${inv.id}`, inv) : api.post('/invoices', inv),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); setEditing(null); },
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => api.put(`/invoices/${id}/paid`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/invoices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const fmt = (n: number) => Number(n).toFixed(2).replace('.', ',');
  const paid = invoices.filter((i) => i.paidAt).length;
  const open = invoices.length - paid;

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Rechnungen</h1>
          <p className="sp-page-subtitle">{invoices.length} gesamt &middot; {open} offen &middot; {paid} bezahlt</p>
        </div>
        <button onClick={() => setEditing({ number: '', projectId: '', totalNet: 0, taxRate: 19, type: 'NORMAL', paymentTermDays: 14, dunningLevel: 0, dunningFee: 0 })} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neue Rechnung
        </button>
      </div>

      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Rechnung bearbeiten' : 'Neue Rechnung'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Rechnungsnr. *</label>
              <input value={editing.number || ''} onChange={(e) => setEditing({ ...editing, number: e.target.value })}
                className="sp-input" placeholder="RE-2026-001" autoFocus />
            </div>
            <div>
              <label className="sp-label">Projekt *</label>
              <select value={editing.projectId || ''} onChange={(e) => setEditing({ ...editing, projectId: e.target.value })}
                className="sp-input">
                <option value="">Projekt wählen...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Nettobetrag (EUR)</label>
              <input type="number" step="0.01" value={editing.totalNet ?? 0}
                onChange={(e) => setEditing({ ...editing, totalNet: +e.target.value })} className="sp-input" />
            </div>
            <div>
              <label className="sp-label">MwSt. %</label>
              <input type="number" value={editing.taxRate ?? 19}
                onChange={(e) => setEditing({ ...editing, taxRate: +e.target.value })} className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Fälligkeitsdatum</label>
              <input type="date" value={(editing.dueDate || '').slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, dueDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Typ</label>
              <select value={editing.type || 'NORMAL'} onChange={(e) => setEditing({ ...editing, type: e.target.value as any })} className="sp-input">
                <option value="NORMAL">Standardrechnung</option>
                <option value="PARTIAL">Abschlagsrechnung</option>
                <option value="FINAL">Schlussrechnung</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Zahlungsziel (Tage)</label>
              <input type="number" value={editing.paymentTermDays ?? 14}
                onChange={(e) => setEditing({ ...editing, paymentTermDays: +e.target.value })} className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Skonto %</label>
              <input type="number" step="0.1" value={editing.skontoPct ?? ''} placeholder="z.B. 2"
                onChange={(e) => setEditing({ ...editing, skontoPct: e.target.value ? +e.target.value : undefined })} className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Leitweg-ID (öffentl. Auftraggeber)</label>
              <input value={editing.leitweg || ''} onChange={(e) => setEditing({ ...editing, leitweg: e.target.value })}
                className="sp-input" placeholder="optional" />
            </div>
            <div>
              <label className="sp-label">Notizen</label>
              <input value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="sp-input" placeholder="Zahlungsbedingungen etc." />
            </div>
          </div>
          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Typ</th>
              <th>Projekt</th>
              <th>Status</th>
              <th className="text-right">Brutto</th>
              <th>Mahnung</th>
              <th className="w-52 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={7}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                </div>
                <span className="text-sm font-medium">Keine Rechnungen vorhanden</span>
              </div></td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} onClick={() => setEditing(inv)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td className="font-mono text-xs font-semibold text-slate-800">{inv.number}</td>
                <td>
                  <span className={`sp-badge border text-[10px] ${typeColors[inv.type] || typeColors.NORMAL}`}>
                    {typeLabels[inv.type] || 'Standard'}
                    {inv.partialNumber ? ` #${inv.partialNumber}` : ''}
                  </span>
                </td>
                <td>
                  <div>
                    <span className="text-slate-500">{inv.project?.title || '-'}</span>
                    {inv.project?.customer?.name && <span className="block text-[11px] text-slate-400">{inv.project.customer.name}</span>}
                  </div>
                </td>
                <td>
                  {inv.paidAt ? (
                    <span className="sp-badge bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Bezahlt
                    </span>
                  ) : (
                    <span className="sp-badge bg-amber-50 text-amber-700 border border-amber-200">
                      {statusLabels[inv.status] || inv.status}
                    </span>
                  )}
                </td>
                <td className="text-right font-mono text-xs font-semibold text-slate-800">{fmt(Number(inv.totalGross))} EUR</td>
                <td>
                  {inv.dunningLevel > 0 && (
                    <span className={`sp-badge border text-[10px] ${
                      inv.dunningLevel === 1 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      inv.dunningLevel === 2 ? 'bg-orange-50 text-orange-700 border-orange-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {inv.dunningLevel === 1 ? 'Erinnerung' : inv.dunningLevel === 2 ? '1. Mahnung' : '2. Mahnung'}
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {!inv.paidAt && (
                      <button onClick={() => markPaid.mutate(inv.id)}
                        className="sp-btn-icon !text-emerald-500 hover:!bg-emerald-50" title="Als bezahlt markieren">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    )}
                    <a href={`/api/v1/invoices/${inv.id}/xrechnung`} target="_blank" rel="noreferrer"
                      className="sp-btn-icon !text-brand-500 hover:!bg-brand-50" title="XRechnung XML">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </a>
                    <a href={`/api/v1/invoices/${inv.id}/datev`} target="_blank" rel="noreferrer" className="sp-btn-icon" title="DATEV CSV">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </a>
                    <button onClick={() => setEditing(inv)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => { if (confirm('Rechnung wirklich löschen?')) deleteMutation.mutate(inv.id); }}
                      className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
